import React, { useEffect, useState } from 'react';
import { ArrowRight, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { heroSlides, company } from '../mock';

const HeroCarousel = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % heroSlides.length), 7000);
    return () => clearInterval(t);
  }, []);

  const next = () => setActive((p) => (p + 1) % heroSlides.length);
  const prev = () => setActive((p) => (p - 1 + heroSlides.length) % heroSlides.length);

  return (
    <section id="home" className="relative pt-28 lg:pt-32 pb-20 overflow-hidden bg-ivory">
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, #17307a 1px, transparent 1px), linear-gradient(to bottom, #17307a 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
        {/* Top eyebrow row */}
        <div className="flex items-center gap-4 mb-12 animate-fade-in">
          <span className="gold-divider"></span>
          <span className="font-mono-label text-gold-dark">{company.tagline}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT: Text */}
          <div className="lg:col-span-7">
            {heroSlides.map((s, i) => (
              <div
                key={i}
                className="hero-slide"
                style={{
                  display: i === active ? 'block' : 'none',
                }}
              >
                <p className="font-mono-label text-gold animate-fade-up" style={{ animationDelay: '0ms' }}>
                  {s.eyebrow}
                </p>
                <h1
                  className="font-serif-display text-[2.4rem] sm:text-5xl lg:text-[4.2rem] leading-[1.05] text-ink mt-5 animate-fade-up"
                  style={{ animationDelay: '120ms' }}
                >
                  {s.title.split(' ').map((w, idx) => (
                    <span key={idx} className={idx % 7 === 3 ? 'italic text-gold-dark font-serif-display font-normal' : ''}>
                      {w}{' '}
                    </span>
                  ))}
                </h1>
                <p
                  className="font-sans-body text-base lg:text-lg text-muted-kmh leading-relaxed mt-7 max-w-xl animate-fade-up"
                  style={{ animationDelay: '240ms' }}
                >
                  {s.description}
                </p>
              </div>
            ))}

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-5 mt-10 animate-fade-up" style={{ animationDelay: '320ms' }}>
              <a
                href="#contact"
                className="btn-premium relative inline-flex items-center gap-3 bg-ink text-ivory hover:text-ivory px-8 py-4 font-mono-label"
                style={{ background: 'var(--kmh-ink)' }}
              >
                <span className="relative z-10">Get in Touch</span>
                <ArrowRight className="w-4 h-4 relative z-10" />
              </a>
              <a
                href={`tel:${company.phone}`}
                className="inline-flex items-center gap-3 text-ink-soft hover:text-gold-dark group"
              >
                <span className="w-12 h-12 border border-line group-hover:border-gold flex items-center justify-center rounded-full">
                  <Phone className="w-4 h-4 text-gold" />
                </span>
                <span>
                  <span className="block font-mono-label text-gold-dark text-[0.65rem]">Requesting a Call</span>
                  <span className="block font-serif-display text-lg text-ink">{company.phone}</span>
                </span>
              </a>
            </div>

            {/* Carousel controls */}
            <div className="flex items-center gap-6 mt-14">
              <button
                onClick={prev}
                aria-label="Previous"
                className="w-12 h-12 rounded-full border border-line text-ink hover:border-gold hover:text-gold-dark flex items-center justify-center"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next"
                className="w-12 h-12 rounded-full border border-line text-ink hover:border-gold hover:text-gold-dark flex items-center justify-center"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3 ml-3">
                <span className="font-mono-label text-ink">{String(active + 1).padStart(2, '0')}</span>
                <span className="w-16 h-px bg-line relative overflow-hidden">
                  <span
                    key={active}
                    className="absolute left-0 top-0 h-full bg-gold"
                    style={{ width: '100%', animation: 'slideRight 7s linear forwards' }}
                  ></span>
                </span>
                <span className="font-mono-label text-muted-kmh">{String(heroSlides.length).padStart(2, '0')}</span>
              </div>
            </div>
          </div>

          {/* RIGHT: Image */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              {heroSlides.map((s, i) => (
                <img
                  key={i}
                  src={s.image}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover hero-slide"
                  style={{
                    opacity: i === active ? 1 : 0,
                    transform: i === active ? 'scale(1)' : 'scale(1.05)',
                  }}
                />
              ))}
              {/* Frame */}
              <div className="absolute inset-0 border border-ivory/20 pointer-events-none"></div>
            </div>
            {/* Decorative number */}
            <div className="absolute -bottom-6 -left-6 lg:-left-10 bg-ivory px-6 py-4 border-l-2 border-gold">
              <p className="font-mono-label text-gold">Since 2020</p>
              <p className="font-serif-display text-ink text-2xl mt-1">500+ Clients</p>
            </div>
            <div className="absolute -top-6 -right-6 lg:-right-10 hidden md:block">
              <div className="w-24 h-24 rounded-full border border-gold/30 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center">
                  <span className="font-serif-display text-gold-dark text-2xl italic">KMH</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
